type ApiAuthenticationProvider = {
  getAuthorizationHeader?: () => Record<string, string> | undefined;
  ensureValidToken?: (options?: {
    force?: boolean;
    redirectOnFailure?: boolean;
  }) => Promise<string | null>;
  handleUnauthenticated?: () => void;
  isPublicRequest?: (url?: string) => boolean;
};

type ApiRequestOptions = {
  uri: string;
  method?: string;
  type?: 'FILE' | string | null;
  data?: any;
  headers?: HeadersInit;
  auth?: 'required' | 'optional' | 'none';
  signal?: AbortSignal;
};

let authenticationProvider: ApiAuthenticationProvider | null = null;

const PUBLIC_PATHS = [
  '/api/login',
  '/api/login/refresh',
  '/api/login/CloudVerify',
  '/api/Share/Access',
  '/getaddress/ip/local/geo/v1/district',
];

export function setApiAuthenticationProvider(provider: ApiAuthenticationProvider | null) {
  authenticationProvider = provider;
}

function isPublicRequest(uri?: string) {
  if (!uri) {
    return false;
  }

  if (authenticationProvider?.isPublicRequest?.(uri)) {
    return true;
  }

  try {
    const parsedUrl = new URL(uri, window.location.origin);
    return PUBLIC_PATHS.some(path => parsedUrl.pathname.startsWith(path));
  } catch {
    return false;
  }
}

function mergeHeaders(...headersList: Array<HeadersInit | undefined>) {
  const headers = new Headers();

  headersList.forEach(headersInit => {
    if (!headersInit) {
      return;
    }

    new Headers(headersInit).forEach((value, key) => headers.set(key, value));
  });

  return headers;
}

function cleanAuthorization(headers: Headers) {
  headers.delete('Authorization');
  headers.delete('authorization');
}

async function buildRequestConfig({
  uri,
  method,
  data,
  headers,
  auth,
  signal,
  forceRefresh,
}: ApiRequestOptions & { method: string; forceRefresh?: boolean }): Promise<RequestInit> {
  const requestIsPublic = isPublicRequest(uri);
  const shouldAuthenticate = auth !== 'none' && !requestIsPublic;
  const requestHeaders = mergeHeaders(headers);

  if (requestIsPublic) {
    cleanAuthorization(requestHeaders);
  }

  if (shouldAuthenticate && authenticationProvider) {
    const token = await authenticationProvider.ensureValidToken?.({
      force: forceRefresh,
      redirectOnFailure: auth === 'required',
    });

    const authorizationHeader = authenticationProvider.getAuthorizationHeader?.();
    if (authorizationHeader) {
      new Headers(authorizationHeader).forEach((value, key) => requestHeaders.set(key, value));
    }

    if (auth === 'required' && !token && !requestHeaders.has('Authorization')) {
      authenticationProvider.handleUnauthenticated?.();
      throw new Error('Authentication required');
    }
  }

  const config: RequestInit = {
    method,
    headers: requestHeaders,
    signal,
    credentials: 'include',
  };

  if (data && !(data instanceof FormData) && !['GET', 'DELETE'].includes(method.toUpperCase())) {
    config.body = JSON.stringify(data);
    requestHeaders.set('Content-Type', 'application/json');
  } else if (data instanceof FormData) {
    config.body = data;
  }

  return config;
}

export async function fetchWithAuthentication(options: ApiRequestOptions): Promise<Response> {
  const { uri, auth = 'required' } = options;
  const method = options.method || 'GET';
  const config = await buildRequestConfig({ ...options, method });
  const response = await fetch(uri, config);

  if (
    response.status !== 401 ||
    auth === 'none' ||
    isPublicRequest(uri) ||
    !authenticationProvider
  ) {
    return response;
  }

  const retryConfig = await buildRequestConfig({
    ...options,
    method,
    forceRefresh: true,
  });
  const retryResponse = await fetch(uri, retryConfig);

  if (retryResponse.status === 401) {
    authenticationProvider.handleUnauthenticated?.();
  }

  return retryResponse;
}

const apiService = async ({
  uri,
  method = 'GET',
  type = 'FILE',
  data = null,
  headers = {},
  auth = 'required',
  signal,
}: ApiRequestOptions) => {
  try {
    const response = await fetchWithAuthentication({
      uri,
      method,
      type,
      data,
      headers,
      auth,
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const contentType = response.headers.get('Content-Type') || '';

    if (method.toUpperCase() === 'GET' && type === 'FILE') {
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;

      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/['"]/g, '')
        : 'downloaded_file.zip';

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      return;
    }

    if (contentType.includes('application/json')) {
      return await response.json();
    } else if (contentType.includes('text/')) {
      return await response.text();
    } else {
      return response;
    }
  } catch (error) {
    // Lifecycle cleanup cancels obsolete requests; callers still need the rejection.
    const isRequestCancelled = signal?.aborted && error?.name === 'AbortError';
    if (!isRequestCancelled) {
      console.error('API call failed:', error);
    }
    throw error;
  }
};

export default apiService;
